import React, { useState, useEffect, useCallback } from "react";
import {
  FiArrowLeft,
  FiBox,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiFileText,
  FiInfo,
  FiMapPin,
  FiPackage,
  FiRefreshCw,
  FiShield,
  FiTruck,
  FiUser,
} from "react-icons/fi";
import axios from "../../../Utils/axiosInstance";
import toast from "react-hot-toast";
import ReturnDeliveredModal from "./ReturnDeliveredModal";

const SectionCard = ({ title, icon, children, badge, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden ${className}`}>
    <div className="px-4 sm:px-6 py-3.5 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-xs sm:text-sm font-extrabold text-gray-900 flex items-center gap-2">
        {icon && <span className="text-base text-primaryColor">{icon}</span>}
        {title}
      </h3>
      {badge && <div>{badge}</div>}
    </div>
    <div className="p-4 sm:p-6">{children}</div>
  </div>
);

const DataRow = ({ label, value, isLast = false }) => (
  <div className={`flex flex-col sm:flex-row sm:items-center justify-between py-2.5 gap-1 ${!isLast ? "border-b border-gray-100" : ""}`}>
    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide min-w-[140px]">{label}</span>
    <span className="text-xs sm:text-sm font-bold text-gray-800 break-words text-left sm:text-right">{value ?? "—"}</span>
  </div>
);

const DetailSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {/* Header Skeleton */}
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 space-y-3 shadow-xs">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="h-4 w-28 bg-gray-200 rounded" />
          <div className="h-4 w-16 bg-gray-200 rounded-full" />
        </div>
        <div className="h-4 w-36 bg-gray-200 rounded" />
      </div>
      <div className="h-6 w-44 bg-gray-200 rounded" />
    </div>

    {/* Banner Skeleton */}
    <div className="h-20 bg-gray-200/70 rounded-2xl" />

    {/* Section 1 Skeleton */}
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-xs">
      <div className="h-4 w-36 bg-gray-200 rounded" />
      <div className="space-y-3 pt-2">
        <div className="h-4 w-full bg-gray-100 rounded" />
        <div className="h-4 w-full bg-gray-100 rounded" />
        <div className="h-4 w-3/4 bg-gray-100 rounded" />
      </div>
    </div>

    {/* Section 2 Skeleton */}
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4 shadow-xs">
      <div className="h-4 w-40 bg-gray-200 rounded" />
      <div className="space-y-3 pt-2">
        <div className="h-4 w-full bg-gray-100 rounded" />
        <div className="h-4 w-full bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
};

const formatUser = (userObj) => {
  if (!userObj) return null;
  if (typeof userObj === "string") {
    if (/^[0-9a-fA-F]{24}$/.test(userObj)) return "Admin / QQT Team";
    return userObj;
  }
  if (userObj.firstName || userObj.lastName) {
    return [userObj.firstName, userObj.lastName].filter(Boolean).join(" ");
  }
  if (userObj.name) return userObj.name;
  if (userObj.email) return userObj.email;
  if (userObj.phone) return userObj.phone;
  return "Admin / QQT Team";
};

const renderAddress = (address) => {
  if (!address) return "Warehouse Address on File";
  if (typeof address === "string") {
    if (/^[0-9a-fA-F]{24}$/.test(address)) return "Warehouse Address on File";
    return address;
  }
  const parts = [
    address.addressLine1 || address.street,
    address.addressLine2,
    address.city,
    address.state,
    address.pincode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Warehouse Address on File";
};

const getOrderNumber = (ord) => {
  if (!ord) return "—";
  if (typeof ord === "object" && ord.orderNumber) return ord.orderNumber;
  if (typeof ord === "string") {
    if (/^[0-9a-fA-F]{24}$/.test(ord)) return `#${ord.slice(-6).toUpperCase()}`;
    return ord;
  }
  return "—";
};

const getQuotationNumber = (quote) => {
  if (!quote) return "—";
  if (typeof quote === "object") {
    return quote.quotationUniqueNumber || quote.quotationNumber || (quote._id ? `#${quote._id.slice(-6).toUpperCase()}` : "—");
  }
  if (typeof quote === "string") {
    if (/^[0-9a-fA-F]{24}$/.test(quote)) return `#${quote.slice(-6).toUpperCase()}`;
    return quote;
  }
  return "—";
};

const getDecisionBadge = (status) => {
  switch (status) {
    case "Return_Request_Approved":
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" };
    case "Return_Request_Rejected":
      return { bg: "bg-red-50 text-red-700 border-red-200", label: "Rejected" };
    case "Return_Request_Pending":
    default:
      return { bg: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending Review" };
  }
};

const getVerificationBadge = (status) => {
  switch (status) {
    case "Approved":
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" };
    case "Rejected":
      return { bg: "bg-red-50 text-red-700 border-red-200", label: "Rejected" };
    default:
      return { bg: "bg-amber-50 text-amber-700 border-amber-200", label: status || "Pending" };
  }
};

const getDispatchBadge = (status) => {
  switch (status) {
    case "Delivered":
      return { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Delivered" };
    case "Dispatched":
      return { bg: "bg-blue-50 text-blue-700 border-blue-200", label: "In Transit / Dispatched" };
    default:
      return { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "Not Initiated" };
  }
};

const SupplierReturnDetailCard = ({ returnId, item: propItem, onBack, onRefresh }) => {
  const effectiveReturnId = returnId || propItem?._id;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deliveredModalOpen, setDeliveredModalOpen] = useState(false);

  // ── Fetch return details by ID ──
  const fetchDetails = useCallback(async (showLoader = true) => {
    if (!effectiveReturnId) return;
    try {
      if (showLoader) setLoading(true);
      setError(false);
      const res = await axios.get(`/return/${effectiveReturnId}`);
      if (res.data?.success && res.data?.data) {
        setItem(res.data.data);
      } else {
        setItem(propItem || null);
      }
    } catch (err) {
      console.error("fetchDetails error:", err);
      setError(true);
      if (propItem) {
        setItem(propItem);
      } else {
        toast.error("Failed to load return dispute details");
      }
    } finally {
      setLoading(false);
    }
  }, [effectiveReturnId, propItem]);

  useEffect(() => {
    fetchDetails(true);
  }, [fetchDetails]);

  if (loading) {
    return <DetailSkeleton />;
  }

  if (error && !item) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center space-y-3">
        <p className="text-sm font-bold text-gray-700">Failed to load return details</p>
        <button
          onClick={() => fetchDetails(true)}
          className="px-4 py-2 bg-primaryColor text-white text-xs font-bold rounded-xl shadow-xs hover:opacity-90"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!item) return null;

  const decisionBadge = getDecisionBadge(item.returnDecision?.status);
  const verificationBadge = getVerificationBadge(item.preInvestigationVerification?.status);
  const dispatchBadge = getDispatchBadge(item.returnDispatch?.status);

  const isDispatched = item.returnDispatch?.status === "Dispatched";
  const isDelivered = item.returnDispatch?.status === "Delivered";
  const isPartial = item.returnType !== "Full_Order";

  const orderNumber = getOrderNumber(item.orderId);
  const quotationNumber = getQuotationNumber(item.quotationId);

  return (
    <div className="space-y-6">
      {/* ── TOP HEADER CARD ── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="md:hidden p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
              >
                <FiArrowLeft className="text-lg" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-extrabold text-primaryColor">
                  {item.uniqueCaseId || `#${(item._id || "").slice(-8).toUpperCase()}`}
                </span>
                <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${decisionBadge.bg}`}>
                  {decisionBadge.label}
                </span>
                {isDelivered && (
                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                    Delivered to Warehouse
                  </span>
                )}
              </div>
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 mt-1">
                Order {orderNumber}
              </h2>
            </div>
          </div>

          <div className="text-xs text-gray-500 sm:text-right">
            <p className="font-medium">Date Raised: <strong className="text-gray-800">{formatDate(item.createdAt) || "—"}</strong></p>
            {quotationNumber !== "—" && (
              <p className="text-[11px] text-gray-400 mt-0.5">Quote Ref: {quotationNumber}</p>
            )}
          </div>
        </div>
      </div>

      {/* ── SUPPLIER DISPATCH STATUS & ACTION BANNER ── */}
      {isDelivered ? (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3 text-xs text-emerald-900 shadow-xs">
          <FiCheckCircle className="text-xl text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-sm text-emerald-950">Package Delivered to Warehouse</p>
            <p className="mt-0.5 text-emerald-800">
              This return lot has been confirmed delivered to your warehouse on {formatDate(item.returnDispatch?.deliveredAt) || "record"}.
            </p>
          </div>
        </div>
      ) : isDispatched ? (
        <div className="p-5 bg-gradient-to-br from-blue-50/90 to-indigo-50/70 border-2 border-blue-300 rounded-2xl space-y-3 shadow-xs">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <FiTruck className="text-2xl text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-extrabold text-sm text-blue-950">Return Lot In Transit</h4>
                <p className="text-xs text-blue-800 mt-0.5 leading-relaxed">
                  The buyer has dispatched this return package. Once you receive the shipment at your warehouse, mark it as delivered below.
                </p>
              </div>
            </div>
          </div>

          <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-blue-200">
            <div className="text-xs text-blue-700 font-medium">
              AWB: <strong className="text-blue-950">{item.returnDispatch?.AWB_Number || "Standard Logistics"}</strong>
            </div>
            <button
              type="button"
              onClick={() => setDeliveredModalOpen(true)}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition shadow-sm shadow-emerald-600/20 flex items-center justify-center gap-1.5"
            >
              <FiCheckCircle className="text-sm" /> Mark as Delivered
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-900 shadow-xs">
          <FiClock className="text-lg text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-sm text-amber-950">Awaiting Buyer Dispatch</p>
            <p className="mt-0.5 text-amber-800 leading-relaxed">
              Dispute is approved. You will be able to confirm receipt once the buyer dispatches the return lot to your warehouse.
            </p>
          </div>
        </div>
      )}

      {/* ── 1. CASE INFORMATION ── */}
      <SectionCard title="Case Information" icon={<FiFileText />}>
        <div className="space-y-0.5">
          <DataRow label="Case ID" value={item.uniqueCaseId || `#${(item._id || "").slice(-8).toUpperCase()}`} />
          <DataRow label="Order Number" value={orderNumber} />
          <DataRow label="Quotation Number" value={quotationNumber} />
          <DataRow label="Date Raised" value={formatDate(item.createdAt) || "—"} isLast />
        </div>
      </SectionCard>

      {/* ── 2. DISPUTE SPECIFICATIONS ── */}
      <SectionCard title="Dispute Specifications" icon={<FiPackage />}>
        <div className="space-y-0.5">
          <DataRow label="Return Type" value={isPartial ? "Partial Return" : "Full Order Return"} />
          {item.packSpecification?.numberOfPacksToBeReturned !== undefined && (
            <DataRow label="Packs to Return" value={`${item.packSpecification.numberOfPacksToBeReturned} Packs`} />
          )}
          {item.packSpecification?.weightOfEachPack !== undefined && (
            <DataRow
              label="Weight / Pack"
              value={`${item.packSpecification.weightOfEachPack} ${item.packSpecification.unitOfWeight || "Kg"}`}
            />
          )}
          {item.reason && (
            <div className="pt-3 mt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reason for Return</p>
              <p className="text-xs sm:text-sm text-gray-800 bg-gray-50 p-3 rounded-xl border border-gray-100 leading-relaxed">
                {item.reason}
              </p>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── 3. PRE-INVESTIGATION VERIFICATION ── */}
      {item.preInvestigationVerification?.status && (
        <SectionCard
          title="Pre-Investigation Verification"
          icon={<FiShield />}
          badge={<span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${verificationBadge.bg}`}>{verificationBadge.label}</span>}
        >
          <div className="space-y-0.5">
            <DataRow label="Verification Status" value={verificationBadge.label} />
            {formatDate(item.preInvestigationVerification?.verifiedAt) && (
              <DataRow label="Verified At" value={formatDate(item.preInvestigationVerification.verifiedAt)} />
            )}
            {formatUser(item.preInvestigationVerification?.verifiedBy) && (
              <DataRow label="Verified By" value={formatUser(item.preInvestigationVerification.verifiedBy)} />
            )}
            {item.preInvestigationVerification?.note && (
              <div className="pt-3 mt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Verification Note</p>
                <p className="text-xs text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100">
                  {item.preInvestigationVerification.note}
                </p>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* ── 4. DISPUTE DECISION ── */}
      {item.returnDecision?.status && (
        <SectionCard
          title="Dispute Decision"
          icon={<FiInfo />}
          badge={<span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${decisionBadge.bg}`}>{decisionBadge.label}</span>}
        >
          <div className="space-y-0.5">
            <DataRow label="Decision Status" value={decisionBadge.label} />
            {formatDate(item.returnDecision?.decidedAt) && (
              <DataRow label="Decided At" value={formatDate(item.returnDecision.decidedAt)} />
            )}
            {formatUser(item.returnDecision?.decidedBy) && (
              <DataRow label="Decided By" value={formatUser(item.returnDecision.decidedBy)} />
            )}
            {item.returnDecision?.note && (
              <div className="pt-3 mt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Decision Note</p>
                <p className="text-xs text-gray-700 bg-gray-50 p-3 rounded-xl border border-gray-100">
                  {item.returnDecision.note}
                </p>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* ── 6. RETURN DISPATCH STATUS & TRACKING ── */}
      <SectionCard
        title="Return Dispatch Logistics"
        icon={<FiTruck />}
        badge={<span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${dispatchBadge.bg}`}>{dispatchBadge.label}</span>}
      >
        <div className="space-y-0.5">
          <DataRow label="Dispatch Status" value={dispatchBadge.label} />
          {item.returnDispatch?.AWB_Number && (
            <DataRow label="AWB Number" value={item.returnDispatch.AWB_Number} />
          )}
          {formatDate(item.returnDispatch?.dispatchedAt) && (
            <DataRow label="Dispatched At" value={formatDate(item.returnDispatch.dispatchedAt)} />
          )}
          {formatDate(item.returnDispatch?.deliveredAt) && (
            <DataRow label="Delivered At" value={formatDate(item.returnDispatch.deliveredAt)} />
          )}
          {formatUser(item.returnDispatch?.deliveredBy) && (
            <DataRow label="Delivered By" value={formatUser(item.returnDispatch.deliveredBy)} />
          )}
          {item.returnDispatch?.trackingUrl && (
            <DataRow
              label="Tracking Link"
              value={
                <a
                  href={
                    item.returnDispatch.trackingUrl.startsWith("http")
                      ? item.returnDispatch.trackingUrl
                      : `https://${item.returnDispatch.trackingUrl}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-bold text-primaryColor hover:underline"
                >
                  <FiExternalLink /> Track Shipment
                </a>
              }
              isLast
            />
          )}
        </div>
      </SectionCard>

      {/* ── DELIVERED CONFIRMATION MODAL ── */}
      <ReturnDeliveredModal
        isOpen={deliveredModalOpen}
        onClose={() => setDeliveredModalOpen(false)}
        returnId={item._id}
        onSuccess={() => {
          fetchDetails(false);
          onRefresh?.();
        }}
      />
    </div>
  );
};

export default SupplierReturnDetailCard;
